import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Archive, Link2, ShieldCheck, GitBranch, DollarSign, Pencil } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import { useAuth } from "@/context/AuthContext";
import { formatDateOnly } from "@/lib/dates";
import { EntityLinkManager } from "@/components/EntityLinkManager";
import { BI_STATUS_COLOR, BI_VERIFY_COLOR } from "./BusinessIntel";

const LINK_MAP = {
  source: { api: "/sources", title: "Sources", label: (o) => o.title },
  claim: { api: "/claims", title: "Claims & Evidence", label: (o) => o.statement },
  claim_tracker: { api: "/claim-tracker/entries", title: "Political Claims", label: (o) => `${o.speaker || ""}: ${o.claim_summary || o.claim_text || ""}` },
  promise: { api: "/promise-tracker/promises", title: "Promises", label: (o) => o.title },
  intel: { api: "/ghana-intel/records", title: "Ghana Intelligence", label: (o) => o.title },
  timeline: { api: "/timeline/timelines", title: "Timelines", label: (o) => o.title },
  media: { api: "/media/assets", title: "Media", label: (o) => o.original_filename },
  content: { api: "/content", title: "Related Content", label: (o) => o.title },
  related_bi: { api: "/business-intel/records", title: "Related Business Intelligence", label: (o) => o.title },
};
const LINK_ORDER = ["source", "claim", "claim_tracker", "promise", "intel", "timeline", "media", "content", "related_bi"];
const VERDICT_COLOR = { true: "text-emerald-400", mostly_true: "text-emerald-300", misleading: "text-amber-400", mostly_false: "text-orange-400", false: "text-red-400", unverified: "text-zinc-400" };

function Section({ title, children, action }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3"><h3 className="font-tactical font-bold text-sm">{title}</h3>{action}</div>
      {children}
    </Card>
  );
}

export default function BusinessIntelDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canReview = user?.roles?.some((r) => ["super_admin", "editor"].includes(r));
  const [meta, setMeta] = useState(null);
  const [rec, setRec] = useState(null);
  const [linkModal, setLinkModal] = useState(null);
  const [stageModal, setStageModal] = useState(null);
  const [finModal, setFinModal] = useState(null);
  const [detailsModal, setDetailsModal] = useState(null);

  const load = useCallback(() => {
    api.get(`/business-intel/records/${id}`).then((r) => setRec(r.data)).catch(() => toast.error("Not found"));
  }, [id]);
  useEffect(() => {
    api.get("/business-intel/meta").then((r) => setMeta(r.data)).catch(() => {});
    load();
  }, [load]);

  const rtl = (t) => meta?.record_type_labels?.[t] || t;
  const saveField = async (patch, msg = "Saved") => {
    try { await api.patch(`/business-intel/records/${id}`, patch); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setVerification = async (verification) => {
    try { await api.post(`/business-intel/records/${id}/verify`, { verification }); toast.success("Verification updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const archive = async () => {
    try { await api.delete(`/business-intel/records/${id}`); toast.success("Archived"); nav("/command-center/business-intelligence"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const openLink = async (type) => {
    try {
      const { data } = await api.get(LINK_MAP[type].api);
      const opts = type === "related_bi" ? data.filter((x) => x.id !== id) : data;
      const selected = (rec.links?.[type] || []).map((x) => x.id);
      setLinkModal({ type, options: opts, selected });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveLinks = async () => {
    try { await api.post(`/business-intel/records/${id}/links`, { link_type: linkModal.type, ids: linkModal.selected }); toast.success("Links updated"); setLinkModal(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleSel = (v) => setLinkModal((m) => ({ ...m, selected: m.selected.includes(v) ? m.selected.filter((x) => x !== v) : [...m.selected, v] }));

  const submitStage = async () => {
    try { await api.post(`/business-intel/records/${id}/stage`, stageModal); toast.success("Stage recorded"); setStageModal(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const submitFinancial = async () => {
    try {
      const p = { ...finModal, amount: finModal.amount === "" ? null : Number(finModal.amount) };
      await api.post(`/business-intel/records/${id}/financials`, p); toast.success("Financial revision recorded"); setFinModal(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openDetails = () => setDetailsModal({
    record_type: rec.record_type || "", sector: rec.sector || "", sub_sector: rec.sub_sector || "",
    country: rec.country || "", region: rec.region || "", location: rec.location || "",
    date_announced: rec.date_announced || "", start_date: rec.start_date || "", end_date: rec.end_date || "",
    priority: rec.priority || "medium", tags: (rec.tags || []).join(", "),
  });
  const submitDetails = async () => {
    try {
      const d = detailsModal;
      if (!d.record_type) { toast.error("Record type is required"); return; }
      const payload = { ...d, tags: d.tags ? d.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
      await api.patch(`/business-intel/records/${id}`, payload);
      toast.success("Business details updated"); setDetailsModal(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const renderLinkItem = (type, it) => {
    if (type === "claim_tracker") return <>{it.label} <span className={`uppercase text-[0.65rem] ${VERDICT_COLOR[it.verdict] || "text-zinc-400"}`}>{it.verdict}</span></>;
    if (type === "promise" || type === "intel" || type === "related_bi") return <>{it.label} <span className="text-zinc-600 text-[0.65rem]">({it.status}{it.verification ? `/${it.verification}` : ""})</span></>;
    if (type === "media") return <>{it.label} <span className="text-zinc-600 text-[0.65rem]">({it.visibility})</span></>;
    return it.label;
  };

  if (!rec) return <div className="p-6 text-zinc-500">Loading…</div>;

  return (
    <div className="p-6" data-testid="bi-detail">
      <button onClick={() => nav("/command-center/business-intelligence")} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"><ArrowLeft className="h-4 w-4" /> Business & Economy Intelligence</button>
      <PageHeader title={rec.title} subtitle={[rtl(rec.record_type), rec.sector, rec.country].filter(Boolean).join(" · ")}>
        <span className={`font-mono-data uppercase text-xs px-2 py-1 rounded-sm border border-zinc-800 ${BI_VERIFY_COLOR[rec.verification]}`} data-testid="bi-verify-badge">{(rec.verification || "").replace(/_/g, " ")}</span>
        {canReview && <Btn variant="danger" data-testid="bi-archive-btn" onClick={archive}><Archive className="h-4 w-4" /> Archive</Btn>}
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Section title="Overview">
            <Field label="Summary"><textarea data-testid="bi-edit-summary" className={inputCls} rows={2} defaultValue={rec.summary || ""} onBlur={(e) => e.target.value !== (rec.summary || "") && saveField({ summary: e.target.value })} /></Field>
            <Field label="Analyst notes"><textarea data-testid="bi-edit-notes" className={inputCls} rows={4} defaultValue={rec.analyst_notes || ""} onBlur={(e) => e.target.value !== (rec.analyst_notes || "") && saveField({ analyst_notes: e.target.value })} /></Field>
            <Field label="Classification"><input data-testid="bi-edit-classification" className={inputCls} defaultValue={rec.classification || ""} onBlur={(e) => e.target.value !== (rec.classification || "") && saveField({ classification: e.target.value })} /></Field>
          </Section>

          <Section title="Verification" action={canReview && <select data-testid="bi-verify-select" className={`${inputCls} w-auto`} value={rec.verification} onChange={(e) => setVerification(e.target.value)}>{(meta?.verifications || []).map((v) => <option key={v} value={v}>{v}</option>)}</select>}>
            <p className="text-[0.7rem] text-zinc-600">Marking Verified requires at least one linked Source or Claims &amp; Evidence record — never inferred from entity links.</p>
            {!rec.has_supporting_evidence && <p className="text-xs text-amber-400 mt-1">No supporting Source/Claims &amp; Evidence linked yet.</p>}
          </Section>

          <Section title="Stage / Status History" action={canReview && <Btn data-testid="bi-stage-btn" onClick={() => setStageModal({ stage: "", note: "" })}><GitBranch className="h-4 w-4" /> Change Stage</Btn>}>
            {(rec.stage_history || []).length === 0 ? <p className="text-xs text-zinc-600">No stage history.</p> : (
              <ol className="relative border-l border-zinc-800 ml-2 space-y-3" data-testid="bi-stage-history">
                {rec.stage_history.map((s, i) => (
                  <li key={i} className="ml-4">
                    <span className="absolute -left-1.5 h-3 w-3 rounded-full bg-zinc-600" />
                    <div className="text-sm text-zinc-100 uppercase font-mono-data text-xs">{s.stage.replace(/_/g, " ")}</div>
                    {s.note && <div className="text-xs text-zinc-500">{s.note}</div>}
                    <div className="text-[0.65rem] text-zinc-600">{s.at ? new Date(s.at).toLocaleString() : ""}</div>
                  </li>
                ))}
              </ol>
            )}
          </Section>

          <Section title="Financial Revision History" action={canReview && <Btn data-testid="bi-fin-btn" onClick={() => setFinModal({ label: "", amount: "", currency: rec.currency || "", as_of: "", source_ref: "", note: "" })}><DollarSign className="h-4 w-4" /> Record Revision</Btn>}>
            {(rec.financial_revisions || []).length === 0 ? <p className="text-xs text-zinc-600">No financial revisions. Current value: {rec.current_value != null ? `${rec.currency || ""} ${Number(rec.current_value).toLocaleString()}` : "—"}</p> : (
              <ul className="space-y-2 text-sm" data-testid="bi-fin-history">
                {rec.financial_revisions.map((f, i) => (
                  <li key={i} className="border border-zinc-800 rounded-sm p-2">
                    <div className="text-zinc-200">{f.label}: <span className="font-mono-data">{f.currency || ""} {f.amount != null ? Number(f.amount).toLocaleString() : "—"}</span></div>
                    <div className="text-[0.65rem] text-zinc-600">{f.as_of ? `as of ${formatDateOnly(f.as_of)} · ` : ""}{f.source_ref ? `ref: ${f.source_ref} · ` : ""}{f.at ? new Date(f.at).toLocaleString() : ""}</div>
                    {f.note && <div className="text-xs text-zinc-500">{f.note}</div>}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <EntityLinkManager linkType="business_intel" recordId={id} note="Link canonical Entity Profiles (companies, banks, executives, ministries, regulators, investors, contractors). Records read live from the entity's cross-system view — no duplication." />

          {LINK_ORDER.map((type) => (
            <Section key={type} title={LINK_MAP[type].title} action={<Btn variant="ghost" data-testid={`bi-link-${type}`} onClick={() => openLink(type)}><Link2 className="h-4 w-4" /> Manage</Btn>}>
              {(rec.links?.[type] || []).length === 0 ? <p className="text-xs text-zinc-600">Nothing linked. Records read live from their originating system.</p> : (
                <ul className="space-y-1 text-sm" data-testid={`bi-linklist-${type}`}>
                  {rec.links[type].map((it) => <li key={it.id} className="text-zinc-300">• {renderLinkItem(type, it)}</li>)}
                </ul>
              )}
            </Section>
          ))}
        </div>

        <div className="space-y-4">
          <Section title="Business Details" action={<Btn variant="ghost" data-testid="bi-edit-details-btn" onClick={openDetails}><Pencil className="h-4 w-4" /> Edit</Btn>}>
            <dl className="text-sm space-y-2">
              {[["Record type", rtl(rec.record_type)], ["Sector", rec.sector], ["Sub-sector", rec.sub_sector], ["Country", rec.country], ["Region", rec.region], ["Location", rec.location]].map(([k, v]) => (
                <div key={k}><dt className="label-caps text-zinc-500">{k}</dt><dd className="text-zinc-300">{v || "—"}</dd></div>
              ))}
              <div><dt className="label-caps text-zinc-500">Current value</dt><dd className="text-zinc-300 font-mono-data">{rec.current_value != null ? `${rec.currency || ""} ${Number(rec.current_value).toLocaleString()}` : "—"}</dd></div>
              <div><dt className="label-caps text-zinc-500">Project stage</dt><dd className="text-zinc-300 uppercase font-mono-data text-xs">{rec.project_stage ? rec.project_stage.replace(/_/g, " ") : "—"}</dd></div>
              {[["Announced", formatDateOnly(rec.date_announced)], ["Start", formatDateOnly(rec.start_date)], ["End", formatDateOnly(rec.end_date)]].map(([k, v]) => (
                <div key={k}><dt className="label-caps text-zinc-500">{k}</dt><dd className="text-zinc-300">{v || "—"}</dd></div>
              ))}
              <div><dt className="label-caps text-zinc-500">Status</dt>
                <dd>{canReview ? <select data-testid="bi-status-select" className={inputCls} value={rec.status} onChange={(e) => saveField({ status: e.target.value }, "Status updated")}>{(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}</select> : <span className={`uppercase font-mono-data text-xs ${BI_STATUS_COLOR[rec.status]}`}>{rec.status}</span>}</dd>
              </div>
              <div><dt className="label-caps text-zinc-500">Priority</dt><dd className="text-zinc-300">{rec.priority}</dd></div>
              <div><dt className="label-caps text-zinc-500">Tags</dt><dd className="text-zinc-300">{rec.tags?.length ? rec.tags.join(", ") : "—"}</dd></div>
              {rec.commodity && <div><dt className="label-caps text-zinc-500">Commodity</dt><dd className="text-zinc-300">{rec.commodity} {rec.unit ? `(${rec.unit})` : ""}</dd></div>}
            </dl>
          </Section>

          <Section title="History (audit)">
            <ul className="space-y-2 text-xs" data-testid="bi-audit">
              {rec.audit_history.length === 0 && <li className="text-zinc-600">No activity.</li>}
              {rec.audit_history.map((a) => (
                <li key={a.id} className="text-zinc-400">
                  <span className="font-mono-data text-emerald-400">{a.action}</span>
                  {a.after?.verification && <span> → {a.after.verification}</span>}
                  {a.after?.stage && <span> → {a.after.stage}</span>}
                  <span className="text-zinc-600"> · {a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>

      <Modal open={!!stageModal} onClose={() => setStageModal(null)} title="Change Project Stage">
        {stageModal && (
          <div className="space-y-4">
            <Field label="Stage"><select data-testid="bi-stage-select" className={inputCls} value={stageModal.stage} onChange={(e) => setStageModal({ ...stageModal, stage: e.target.value })}><option value="">Select…</option>{(meta?.project_stages || []).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select></Field>
            <Field label="Note"><input data-testid="bi-stage-note" className={inputCls} value={stageModal.note} onChange={(e) => setStageModal({ ...stageModal, note: e.target.value })} /></Field>
            <p className="text-[0.7rem] text-zinc-600">Stage changes are append-only — history is never overwritten. Delayed/stalled must be set by an editor, never inferred.</p>
            <Btn data-testid="bi-stage-save" onClick={submitStage} disabled={!stageModal.stage} className="w-full justify-center">Record Stage</Btn>
          </div>
        )}
      </Modal>

      <Modal open={!!finModal} onClose={() => setFinModal(null)} title="Record Financial Revision">
        {finModal && (
          <div className="space-y-4">
            <Field label="Label"><input data-testid="bi-fin-label" className={inputCls} value={finModal.label} onChange={(e) => setFinModal({ ...finModal, label: e.target.value })} placeholder="e.g. revised contract value" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount"><input type="number" data-testid="bi-fin-amount" className={inputCls} value={finModal.amount} onChange={(e) => setFinModal({ ...finModal, amount: e.target.value })} /></Field>
              <Field label="Currency"><input data-testid="bi-fin-currency" className={inputCls} value={finModal.currency} onChange={(e) => setFinModal({ ...finModal, currency: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="As of date"><input type="date" data-testid="bi-fin-asof" className={inputCls} value={finModal.as_of} onChange={(e) => setFinModal({ ...finModal, as_of: e.target.value })} /></Field>
              <Field label="Source reference"><input data-testid="bi-fin-ref" className={inputCls} value={finModal.source_ref} onChange={(e) => setFinModal({ ...finModal, source_ref: e.target.value })} /></Field>
            </div>
            <Field label="Note"><input data-testid="bi-fin-note" className={inputCls} value={finModal.note} onChange={(e) => setFinModal({ ...finModal, note: e.target.value })} /></Field>
            <p className="text-[0.7rem] text-zinc-600">Revisions are append-only and retain amount, currency, as-of date and source reference.</p>
            <Btn data-testid="bi-fin-save" onClick={submitFinancial} disabled={!finModal.label} className="w-full justify-center">Record Revision</Btn>
          </div>
        )}
      </Modal>

      <Modal open={!!detailsModal} onClose={() => setDetailsModal(null)} title="Edit Business Details" wide>
        {detailsModal && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Record type"><select data-testid="bi-d-type" className={inputCls} value={detailsModal.record_type} onChange={(e) => setDetailsModal({ ...detailsModal, record_type: e.target.value })}>{(meta?.record_types || []).map((t) => <option key={t} value={t}>{rtl(t)}</option>)}</select></Field>
              <Field label="Sector"><select data-testid="bi-d-sector" className={inputCls} value={detailsModal.sector} onChange={(e) => setDetailsModal({ ...detailsModal, sector: e.target.value })}><option value="">—</option>{(meta?.sectors || []).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
              <Field label="Sub-sector"><input data-testid="bi-d-subsector" className={inputCls} value={detailsModal.sub_sector} onChange={(e) => setDetailsModal({ ...detailsModal, sub_sector: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Country"><input data-testid="bi-d-country" className={inputCls} value={detailsModal.country} onChange={(e) => setDetailsModal({ ...detailsModal, country: e.target.value })} /></Field>
              <Field label="Region"><select data-testid="bi-d-region" className={inputCls} value={detailsModal.region} onChange={(e) => setDetailsModal({ ...detailsModal, region: e.target.value })}><option value="">—</option>{(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
              <Field label="Location"><input data-testid="bi-d-location" className={inputCls} value={detailsModal.location} onChange={(e) => setDetailsModal({ ...detailsModal, location: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Date announced"><input type="date" data-testid="bi-d-announced" className={inputCls} value={detailsModal.date_announced} onChange={(e) => setDetailsModal({ ...detailsModal, date_announced: e.target.value })} /></Field>
              <Field label="Start date"><input type="date" data-testid="bi-d-start" className={inputCls} value={detailsModal.start_date} onChange={(e) => setDetailsModal({ ...detailsModal, start_date: e.target.value })} /></Field>
              <Field label="End / completion date"><input type="date" data-testid="bi-d-end" className={inputCls} value={detailsModal.end_date} onChange={(e) => setDetailsModal({ ...detailsModal, end_date: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Priority"><select data-testid="bi-d-priority" className={inputCls} value={detailsModal.priority} onChange={(e) => setDetailsModal({ ...detailsModal, priority: e.target.value })}>{(meta?.priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
              <Field label="Tags (comma-separated)"><input data-testid="bi-d-tags" className={inputCls} value={detailsModal.tags} onChange={(e) => setDetailsModal({ ...detailsModal, tags: e.target.value })} /></Field>
            </div>
            <p className="text-[0.7rem] text-zinc-600">Editing core metadata is audited (old → new). Stage history and financial revisions are append-only and unaffected. Dates are stored as plain calendar days (no timezone shift).</p>
            <Btn data-testid="bi-d-save" onClick={submitDetails} className="w-full justify-center">Save Details</Btn>
          </div>
        )}
      </Modal>

      <Modal open={!!linkModal} onClose={() => setLinkModal(null)} title={`Link ${linkModal ? LINK_MAP[linkModal.type].title : ""}`} wide>
        {linkModal && (
          <div className="space-y-3">
            <div className="max-h-80 overflow-y-auto space-y-1" data-testid="bi-link-options">
              {linkModal.options.length === 0 && <p className="text-xs text-zinc-600">Nothing available. Create one in its own workspace first.</p>}
              {linkModal.options.map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-sm border border-zinc-800 px-3 py-2 rounded-sm cursor-pointer hover:border-zinc-600">
                  <input type="checkbox" checked={linkModal.selected.includes(o.id)} onChange={() => toggleSel(o.id)} />
                  <span className="truncate">{LINK_MAP[linkModal.type].label(o)}</span>
                </label>
              ))}
            </div>
            <Btn data-testid="bi-save-links" onClick={saveLinks} className="w-full justify-center">Save Links</Btn>
          </div>
        )}
      </Modal>
    </div>
  );
}
